import { useState } from "react";
import { BeatShell, ChipRow } from "@/components/nutrition/NutritionParts";
import { gradeChips, beatMax } from "../beatUtils";
import { Briefing } from "./Shared";
import { Radiation } from "lucide-react";

// ALARA dose meter: picking unsafe practices pushes staff dose up into the red.
export function AlaraBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;
  const toggle = (i) => { if (checked) return; setSel((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; }); };

  // dose model: unsafe (flag=false) picks raise dose, safe (flag=true) picks lower it
  const unsafe = [...sel].filter((i) => !beat.chips[i].flag).length;
  const safe = [...sel].filter((i) => beat.chips[i].flag).length;
  const dose = Math.max(0.2, Math.min(10, 1 + unsafe * 3 - safe * 0.45));
  const pct = (dose / 10) * 100;
  const zone = dose <= 2 ? { c: "#10b981", t: "Low — ALARA" } : dose <= 5 ? { c: "#f59e0b", t: "Elevated" } : { c: "#ef4444", t: "Unsafe dose" };

  const onPrimary = () => {
    if (!checked) {
      const g = gradeChips(beat.chips, sel, beat.max ?? beat.full ?? 20);
      setRes({ earned: g.pts, max: beatMax({ ...beat, type: "multi" }), detail: `Caught ${g.caught}/${g.total}, ${g.falseTaps} unsafe picks`, key: beat.key, label: beat.label });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={Radiation}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Check practices"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      {/* live staff dose meter */}
      <div className="rounded-lg border-2 border-slate-200 bg-white p-3" data-testid="dose-meter">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Staff dose meter</span>
          <span className="text-xs font-bold" style={{ color: zone.c }} data-testid="dose-state">{zone.t}</span>
        </div>
        <div className="relative h-3 rounded-full overflow-hidden" style={{ background: "linear-gradient(90deg,#10b981 0%,#10b981 20%,#f59e0b 50%,#ef4444 100%)" }}>
          <div className="absolute top-0 bottom-0 right-0 bg-white/70" style={{ left: `${pct}%` }} />
          <div className="absolute top-[-3px] bottom-[-3px] w-1 bg-slate-900 rounded transition-all duration-300" style={{ left: `calc(${pct}% - 2px)` }} data-testid="dose-needle" />
        </div>
        <div className="text-[10px] text-slate-400 mt-1 font-mono-clinical">Relative exposure rises when you select an unsafe practice.</div>
      </div>

      <ChipRow chips={beat.chips} selected={sel} onToggle={toggle} revealed={checked} testidPrefix={`${beat.key}-chip`} />
      {checked && (
        <div className="mt-1 space-y-1.5" data-testid={`${beat.key}-coaching`}>
          {beat.chips.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
            </div>
          ))}
        </div>
      )}
    </BeatShell>
  );
}
